import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ReactNode, createContext, useState, useEffect, useMemo, useCallback } from 'react';

export interface ICollapseDrawerContextProps {
  isCollapse?: boolean;
  collapseClick: boolean;
  collapseHover: boolean;
  onToggleCollapse: VoidFunction;
  onHoverEnter: VoidFunction;
  onHoverLeave: VoidFunction;
}

const initialState: ICollapseDrawerContextProps = {
  collapseClick: false,
  collapseHover: false,
  onToggleCollapse: () => Object,
  onHoverEnter: () => Object,
  onHoverLeave: () => Object,
};

const CollapseDrawerContext = createContext(initialState);

export interface ICollapseDrawerProviderProps {
  readonly children: ReactNode;
}

function CollapseDrawerProvider({ children }: ICollapseDrawerProviderProps): JSX.Element {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [collapse, setCollapse] = useState({
    click: false,
    hover: false,
  });

  useEffect(() => {
    if (isMobile) {
      setCollapse({
        click: false,
        hover: false,
      });
    }
  }, [isMobile]);

  const handleToggleCollapse = useCallback((): void => {
    setCollapse((prevCollapse) => ({ ...prevCollapse, click: !prevCollapse.click }));
  }, []);

  const handleHoverEnter = useCallback((): void => {
    if (collapse.click) {
      setCollapse({ ...collapse, hover: true });
    }
  }, [collapse]);

  const handleHoverLeave = useCallback((): void => {
    setCollapse({ ...collapse, hover: false });
  }, [collapse]);

  const collapseDrawerValue = useMemo(
    () => ({
      isCollapse: collapse.click && !collapse.hover,
      collapseClick: collapse.click,
      collapseHover: collapse.hover,
      onToggleCollapse: handleToggleCollapse,
      onHoverEnter: handleHoverEnter,
      onHoverLeave: handleHoverLeave,
    }),
    [collapse, handleHoverEnter, handleToggleCollapse, handleHoverLeave],
  );

  return <CollapseDrawerContext.Provider value={collapseDrawerValue}>{children}</CollapseDrawerContext.Provider>;
}

export { CollapseDrawerProvider, CollapseDrawerContext };
